/**
 * This file contains a variety of hacky deductions to derive
 * 1. `Effect` class provided by preact
 * 2. `_callback` property defined on the class (which is not `_callback` in
 *    reality due to preact's minification)
 * 3. `_dispose` property defined on the class (which is also not `_dispose`)
 */
import { effect } from "@preact/signals-core";

// Effect returns a bound `_dispose` function
let disposePropertyName =
	effect.toString().match(/return .+?\.(.+?)\.bind/)?.[1] || "";

let PreactEffect: {
	new (fn: () => void): {
		/**
		 * These methods don't actually exist, but we pretend they do
		 * so that the users of this file think we are sane
		 */
		_dispose(): void;
		_callback(): void;
	};
};

let callbackPropertyName = "";

// When the provided effect function is ran, `this` is an instance of
// preact's `Effect` class
let effectFn = function (this: any) {
	PreactEffect = this.constructor;

	let effectPropertyName: string = "";

	// One of the properties on `this` will be the actual effect
	// functions. We will need to determine the "_callback" function name
	for (let property in this) {
		if (this[property] === effectFn) {
			effectPropertyName = property;
		}
	}

	for (let property of Object.getOwnPropertyNames(PreactEffect.prototype)) {
		const value = PreactEffect.prototype[property];
		// The callback function should be the only one that calls `this.[effectFn key]()`
		// aside from the constructor
		if (
			property !== "constructor" &&
			typeof value === "function" &&
			value.toString().indexOf(`this.${effectPropertyName}()`)
		) {
			callbackPropertyName = property as "_callback";
			break;
		}
	}
};

// Creating a mock effect to derive everything we need
const preactEffect = effect(effectFn);
preactEffect();

export const getDisposePropertyName = () => {
	// The returned type is fiction for the purpose of sanity
	return disposePropertyName as "_dispose";
};

export const getCallbackName = () => {
	// The returned type is fiction for the purpose of sanity
	return callbackPropertyName as "_callback";
};

export const getPreactEffectClass = () => PreactEffect;
